import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Select, MenuItem,
  FormControl, Chip, Avatar, Paper, Grid, Drawer, Modal, Tooltip,
  CircularProgress, Divider, InputAdornment, Badge, Rating,
  Collapse, Snackbar, Alert, useMediaQuery, useTheme, AppBar,
  Toolbar, ToggleButton, ToggleButtonGroup, Stack, Menu,
} from '@mui/material';
import {
  Search, Refresh, Close, Download, Share, ExpandMore,
  ThumbUp, ThumbDown, Comment, Send, FilterList,
  CheckCircle, HourglassEmpty, Cancel, Menu as MenuIcon,
  MoreVert,
} from '@mui/icons-material';
import { createTheme, ThemeProvider, alpha } from '@mui/material/styles';
import { AiActions } from '../../Services/_actions/index.js';
import BeautifyText from '../AiChatbot/BeautifyText.tsx';
import FeedbackSection from './FeedbackSection.tsx';
import CommonTable, { EllipsisWithTooltip } from '../Shared/Tables/TableV1.tsx';
import PresentationViewer from './PresentationViewer.tsx';
import BeautifyMarkdown from '../AiChatbot/Beautifymarkdown.tsx';

// ── MUI Theme ─────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: { main: '#4962e5' },
    success: { main: '#16a34a' },
    warning: { main: '#ca8a04' },
    error: { main: '#dc2626' },
    background: { default: '#f4f5f7', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
  },
  typography: {
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 700 } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 700 } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────
interface QueryRecord {
  id: string;
  conversation_id: string;
  query: string;
  response: string;
  type: string;
  status: 'completed' | 'wip' | 'failed';
  created_at: string;
  updated_at?: string;
  ai_model?: string;
  parameters?: Record<string, any>;
  file_url?: string;
  image_url?: string;
  presentation?: {
    title: string;
    subtitle: string;
    slides: any[];
    filename?: string;
  } | null;
  pptFilename?: string;
}

interface TeamMember {
  id: string;
  name: string;
  avatar: string;
  color: string;
}

interface AdminGroup {
  id: string;
  name: string;
  avatar: string;
  color: string;
  team: string;
  members: TeamMember[];
}

interface FeedbackRecord {
  queryId: string;
  rating: number | null;
  thumbs: 'up' | 'down' | null;
  comment: string;
  submitted: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  completed: { label: 'Completed', bg: '#dcfce7', color: '#14532d', border: '#86efac', dot: '#16a34a' },
  wip: { label: 'In Progress', bg: '#fef9c3', color: '#713f12', border: '#fde047', dot: '#ca8a04' },
  failed: { label: 'Failed', bg: '#fee2e2', color: '#7f1d1d', border: '#fca5a5', dot: '#dc2626' },
};

const TYPE_COLOR: Record<string, string> = {
  'chat': '#7C3AED', 'image': '#2563EB', 'presentation': '#D97706',
  'research': '#059669', 'video': '#DB2777',
  'PR & Branding': '#7C3AED', 'Sales & Marketing': '#2563EB',
  'Research': '#059669', 'Design & Development': '#D97706',
  'Finance': '#DC2626', 'Operations': '#0891B2',
  'HR & Training': '#6D28D9', 'Media Production': '#DB2777',
  'Legal & Compliance': '#374151',
};

const TEAM_STRUCTURE: AdminGroup[] = [
  {
    id: 'a1', name: 'Rahul Sharma', avatar: 'RS', color: '#7C3AED', team: 'Brand & Marketing',
    members: [
      { id: 'm1', name: 'Priya Nair', avatar: 'PN', color: '#2563EB' },
      { id: 'm2', name: 'Amit Verma', avatar: 'AV', color: '#059669' },
    ],
  },
  {
    id: 'a2', name: 'Sneha Kapoor', avatar: 'SK', color: '#D97706', team: 'Sales & Research',
    members: [
      { id: 'm3', name: 'Karan Mehta', avatar: 'KM', color: '#DC2626' },
      { id: 'm4', name: 'Divya Singh', avatar: 'DS', color: '#6D28D9' },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function deriveStatus(item: any): 'completed' | 'wip' | 'failed' {
  if (item.status) return item.status;
  if (item.error || item.failed) return 'failed';
  const response = item?.response;
  // PPT response
  if (response?.presentation || response?.file_url) return 'wip';
  const responseText = typeof response === 'object'
    ? response?.response
    : response;
  if (responseText && responseText.trim().length > 0) return 'wip';
  return 'completed';
}

function deriveType(item: any): string {
  if (item.type) return item.type;
  if (item.parameters?.Dashboard) return item.parameters.Dashboard;
  if (item.ai_model) return item.ai_model;
  return 'chat';
}

function formatDate(d: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(d: string) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── StatCard ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, icon, sub }: { label: string; value: number; color: string; icon: React.ReactNode; sub?: string }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: '13px', p: '16px 20px', display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 160px', minWidth: 0, border: '1px solid #e2e8f0' }}>
      <Box sx={{ width: 46, height: 46, borderRadius: '12px', bgcolor: alpha(color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${alpha(color, 0.18)}` }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, letterSpacing: 0.9, textTransform: 'uppercase', mb: 0.25 }}>{label}</Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 900, color: '#0f172a', lineHeight: 1, mb: 0.25 }}>{value.toLocaleString()}</Typography>
        {sub && <Typography sx={{ fontSize: 11, color, fontWeight: 600 }}>{sub}</Typography>}
      </Box>
    </Paper>
  );
}

// ── PreviewPanel ──────────────────────────────────────────────────────────────
function PreviewPanel({ query, onClose, onDl, onShare, feedback, onFeedbackChange, onFeedbackSubmit }: {
  query: QueryRecord;
  onClose: () => void;
  onDl: (q: QueryRecord) => void;
  onShare: (q: QueryRecord) => void;
  feedback: FeedbackRecord;
  onFeedbackChange: (f: Partial<FeedbackRecord>) => void;
  onFeedbackSubmit: () => void;
}) {
  const s = STATUS_CONFIG[query.status] || STATUS_CONFIG.completed;
  const tc = TYPE_COLOR[query.type] || '#374151';

  return (
    <Paper variant="outlined" sx={{
      width: { xs: '100%', md: 380 }, flexShrink: 0, borderRadius: '13px',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      height: 'calc(100vh - 230px)',
      maxHeight: 'calc(100vh - 200px)',
      border: '1px solid #e2e8f0',
    }}>
      {/* Header */}
      <Box sx={{ p: '14px 16px 12px', borderBottom: '1px solid #f3f4f6', bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.25 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#111827', lineHeight: 1.45, flex: 1 }}>{query.query}</Typography>
          <IconButton size="small" onClick={onClose} sx={{ width: 26, height: 26, border: '1px solid #e5e7eb', borderRadius: '6px', bgcolor: '#fff', color: '#6b7280', fontSize: 12 }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.25 }}>
          <Chip label={query.type || 'chat'} size="small" sx={{ fontSize: 10, fontWeight: 700, bgcolor: alpha(tc, 0.12), color: tc, border: `1px solid ${alpha(tc, 0.2)}`, height: 20 }} />
          <Chip label={s.label} size="small" sx={{ fontSize: 10, fontWeight: 700, bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`, height: 20 }} />
          {query.ai_model && <Chip label={query.ai_model} size="small" sx={{ fontSize: 10, bgcolor: '#f3f4f6', color: '#6b7280', fontWeight: 600, height: 20 }} />}
        </Box>

        <Grid container spacing={1} sx={{ p: '10px 12px', bgcolor: '#fff', border: '1px solid #f3f4f6', borderRadius: '9px', mb: 1.25 }}>
          {[['Date', formatDateTime(query.created_at)], ['Conversation ID', (query.conversation_id?.slice(0, 14) || '') + '…']].map(([l, v]) => (
            <Grid item xs={6} key={l}>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.25 }}>{l}</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1f2937', wordBreak: 'break-all' }}>{v}</Typography>
            </Grid>
          ))}
        </Grid>

        {query.image_url && (
          <Box sx={{ mb: 1.25, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <Box component="img" src={query.image_url} alt="Generated" sx={{ width: '100%', display: 'block', maxHeight: 160, objectFit: 'cover' }} />
          </Box>
        )}

        <Stack direction="row" spacing={0.75}>
          <Chip
            icon={<Download sx={{ fontSize: 12, '&&': { color: '#374151' } }} />}
            label="Download"
            size="small"
            onClick={() => onDl(query)}
            sx={{ fontSize: 10, fontWeight: 700, height: 22, bgcolor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', cursor: 'pointer', '&:hover': { bgcolor: alpha('#7C3AED', 0.08), borderColor: '#7C3AED', color: '#7C3AED' } }}
          />
          <Chip
            icon={<Share sx={{ fontSize: 12, '&&': { color: '#fff' } }} />}
            label="Share"
            size="small"
            onClick={() => onShare(query)}
            sx={{ fontSize: 10, fontWeight: 700, height: 22, bgcolor: '#7C3AED', color: '#fff', border: '1px solid #6D28D9', cursor: 'pointer', '&:hover': { bgcolor: '#6D28D9' } }}
          />
        </Stack>
      </Box>

      {/* Response */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: '14px 16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, mb: 1.5 }}>
          <Box sx={{ width: 4, height: 14, bgcolor: '#7C3AED', borderRadius: '2px' }} />
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 1 }}>
            AI Response
          </Typography>
        </Box>

        {/* Normal text response */}
        {!query.presentation && query.response && query.response !== '__PRESENTATION__' && (
          // <BeautifyText text={query.response} />
          <BeautifyMarkdown text={query.response} />
        )}

        {/* PPT response*/}
        {query.presentation && (
          <Box sx={{ mt: query.response && query.response !== '__PRESENTATION__' ? 2 : 0 }}>
            <PresentationViewer
              title={query.presentation.title}
              subtitle={query.presentation.subtitle}
              pptUrl={query.file_url}
              pptFilename={query.pptFilename || query.presentation.filename}
            />
          </Box>
        )}

        {!query.presentation && (!query.response || query.response === '__PRESENTATION__') && (
          <Typography sx={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
            No response content available.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ── ShareModal ────────────────────────────────────────────────────────────────
function ShareModal({ query, onClose }: { query: QueryRecord; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `https://machadalo.com/studio/q/${query.conversation_id}`;
  const platforms = [
    { name: 'LinkedIn', icon: '🔗', color: '#0077B5', url: `https://linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { name: 'Instagram', icon: '📸', color: '#E1306C', url: 'https://instagram.com' },
    { name: 'Email', icon: '✉', color: '#059669', url: `mailto:?subject=${encodeURIComponent('Query: ' + query.query)}&body=${encodeURIComponent('AI Query Result:\n\n' + query.query + '\n\nView at: ' + shareUrl)}` },
  ];

  return (
    <Modal open onClose={onClose}>
      <Box sx={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2.5 }}>
        <Paper sx={{ borderRadius: '16px', p: 3.5, maxWidth: 400, width: '100%', boxShadow: '0 32px 80px rgba(0,0,0,0.2)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>Share Query</Typography>
            <IconButton size="small" onClick={onClose} sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', color: '#6b7280' }}>
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>

          <Paper variant="outlined" sx={{ p: '10px 14px', mb: 2, borderRadius: '9px', bgcolor: '#f9fafb', border: '1px solid #f3f4f6' }}>
            <Typography sx={{ fontSize: 13, color: '#1f2937', fontWeight: 600, lineHeight: 1.5 }}>{query.query}</Typography>
          </Paper>

          <Stack direction="row" spacing={1.25} sx={{ mb: 2 }}>
            {platforms.map((p) => (
              <Box key={p.name} component="a" href={p.url} target="_blank" rel="noreferrer"
                sx={{ flex: 1, p: '12px 6px', borderRadius: '10px', bgcolor: alpha(p.color, 0.05), border: `1.5px solid ${alpha(p.color, 0.25)}`, textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.625, cursor: 'pointer', '&:hover': { bgcolor: alpha(p.color, 0.1) } }}>
                <Typography sx={{ fontSize: 22 }}>{p.icon}</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: p.color }}>{p.name}</Typography>
              </Box>
            ))}
          </Stack>

          <Stack direction="row" spacing={1}>
            <Box sx={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '8px', p: '9px 12px', fontSize: 12, color: '#6b7280', bgcolor: '#fafafa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>{shareUrl}</Box>
            <Button variant="contained" size="small"
              onClick={() => { navigator.clipboard?.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              sx={{ fontSize: 12, fontWeight: 700, borderRadius: '8px', bgcolor: copied ? '#059669' : '#7C3AED', '&:hover': { bgcolor: copied ? '#047857' : '#6D28D9' }, whiteSpace: 'nowrap', transition: 'background 0.2s', px: 2 }}>
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Modal>
  );
}

// ── MAIN DASHBOARD ─────────────────────────────────────────────────────────────
const QueryHistoryDashboard: React.FC = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isSmall = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const action = AiActions();
  const userInfo = localStorage?.getItem('userInfo');
  const parsedUser = userInfo ? JSON.parse(userInfo) : {};
  const ORGANIZATION_ID: string = parsedUser?.profile?.organisation?.organisation_id || '';
  const userRole: string = parsedUser?.role ?? parsedUser?.profile?.role ?? null;
  const userId: number = parsedUser?.id || 0;
  const isSuperUser: boolean = parsedUser?.is_superuser || false;

  let effectiveRole = userRole;

  if (!effectiveRole && isSuperUser) {
    effectiveRole = 'super_admin';
  } else if (!effectiveRole) {
    effectiveRole = 'individual';
  }

  const role: 'superAdmin' | 'projectAdmin' | 'individual' =
    effectiveRole === 'super_admin' ? 'superAdmin' :
      effectiveRole === 'project_admin' ? 'projectAdmin' :
        'individual';

  const [selAdmin, setSelAdmin] = useState<string | null>(null);
  const [selMember, setSelMember] = useState<string | null>(null);
  const [statusF, setStatusF] = useState('all');
  const [typeF, setTypeF] = useState('all');
  const [search, setSearch] = useState('');
  const [selQuery, setSelQuery] = useState<QueryRecord | null>(null);
  const [shareQ, setShareQ] = useState<QueryRecord | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [feedbacks, setFeedbacks] = useState<Record<string, FeedbackRecord>>({});

  const [conversations, setConversations] = useState<QueryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyCache, setHistoryCache] = useState<Record<string, QueryRecord[]>>({});
  const [feedbackModalQuery, setFeedbackModalQuery] = useState<QueryRecord | null>(null);

  const columns = useMemo(() => [
    {
      accessorKey: 'query',
      header: 'Query',
      size: 100,
      Cell: ({ row }: { row: any }) => {
        const q = row.original as QueryRecord;

        return (
          <Typography
            variant="body2"
            color="primary.main"
            sx={{ cursor: 'pointer' }}
            onClick={() => handleRowClick(row.original)}
          >
            <EllipsisWithTooltip
              text={q.query}
              maxWidth={200}
            />
          </Typography>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 120,
      Cell: ({ row }: { row: any }) => {
        const tc = TYPE_COLOR[row.original.type] || '#374151';
        return (
          <Chip label={row.original.type || 'chat'} size="small" sx={{ fontSize: 10, fontWeight: 700, bgcolor: alpha(tc, 0.1), color: tc, border: `1px solid ${alpha(tc, 0.18)}`, maxWidth: 110 }} />
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 110,
      Cell: ({ row }: { row: any }) => {
        const s = STATUS_CONFIG[row.original.status] || STATUS_CONFIG.completed;
        return (
          <Chip
            label={s.label}
            size="small"
            sx={{ fontSize: 10, fontWeight: 700, bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`, height: 20 }}
          />
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Date',
      size: 110,
      Cell: ({ row }: { row: any }) => (
        <Typography sx={{ fontSize: 11.5, color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {formatDate(row.original.created_at)}
        </Typography>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 110,
      enableSorting: false,
      Cell: ({ row }: { row: any }) => {
        const q = row.original as QueryRecord;
        return (
          <Box sx={{ display: 'flex', gap: 0.625, justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
            <Tooltip title="Download response">
              <IconButton size="small" onClick={() => handleDl(q)}
                sx={{ width: 30, height: 30, border: '1px solid #e2e8f0', borderRadius: '7px', bgcolor: '#fff', color: '#475569', '&:hover': { borderColor: '#7C3AED', color: '#7C3AED' } }}>
                <Download sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share">
              <IconButton size="small" onClick={() => setShareQ(q)}
                sx={{ width: 30, height: 30, border: '1px solid #e2e8f0', borderRadius: '7px', bgcolor: '#fff', color: '#475569', '&:hover': { borderColor: '#7C3AED', color: '#7C3AED' } }}>
                <Share sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Feedback">
              <IconButton size="small" onClick={e => { e.stopPropagation(); setFeedbackModalQuery(q); }}
                sx={{ width: 30, height: 30, border: '1px solid #e2e8f0', borderRadius: '7px', bgcolor: '#fff', color: getFeedback(q.id).submitted ? '#16a34a' : '#475569', '&:hover': { borderColor: '#7C3AED', color: '#7C3AED' } }}>
                <ThumbUp sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [feedbacks, selQuery]);

  const getFeedback = (queryId: string): FeedbackRecord =>
    feedbacks[queryId] || { queryId, rating: null, thumbs: null, comment: '', submitted: false };

  const updateFeedback = (queryId: string, changes: Partial<FeedbackRecord>) => {
    setFeedbacks(prev => ({ ...prev, [queryId]: { ...getFeedback(queryId), ...changes } }));
  };

  const submitFeedback = async (queryId: string) => {
    try {
      const feedback = getFeedback(queryId);

      const payload = {
        id: queryId,
        feedback: {
          user_id: userId,
          rating: feedback.rating,
          comment: feedback.comment,
          is_feedback: true,
        },
      };

      const res = await action.submitFeedback(payload);

      if (res?.status === 200 || res?.status === true) {
        updateFeedback(queryId, { submitted: true });

        setSnackbar({
          open: true,
          message: 'Feedback submitted successfully!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Feedback Error:', error);

      setSnackbar({
        open: true,
        message: 'Failed to submit feedback',
        severity: 'error',
      });
    }
  };

  const fetchConversations = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await action.getConversationList({ parameters: { dashboard: 'machadalo_studio' } });
      const raw: any[] = res?.data?.results || res?.data || [];
      const mapped: QueryRecord[] = raw.map((item: any, idx: number) => ({
        id: item._id || item.id || String(idx),
        conversation_id: item.conversation_id || item._id || String(idx),
        query: item.query || item.prompt || 'Untitled query',
        response: item.response || '',
        type: deriveType(item),
        status: deriveStatus(item),
        created_at: item.created_at || item.timestamp || '',
        updated_at: item.updated_at || '',
        ai_model: item.ai_model || '',
        parameters: item.parameters || {},
        image_url: item.image_url || '',
        file_url: item.file_url || '',
      }));
      setConversations(mapped);
    } catch (err: any) {
      setError('Failed to load conversation history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (conversationId: string) => {
    if (historyCache[conversationId]) return historyCache[conversationId];
    try {
      const res = await action.getChatHistory({
        parameters: { dashboard: 'machadalo_studio' },
        ai_model: 'mdai',
        conversation_id: conversationId,
      });
      if (res?.status === 200 && Array.isArray(res?.data)) {
        const items: QueryRecord[] = res.data.map((item: any, idx: number) => {
          const response = item?.response;
          const presentation = response?.presentation;
          const pptUrl = response?.file_url;
          const pptFilename = response?.filename;

          const responseText = presentation
            ? '__PRESENTATION__'
            : typeof response === 'object'
              ? response?.response || ''
              : response || '';

          return {
            id: item._id || String(idx),
            conversation_id: conversationId,
            query: item.query || '',
            response: responseText,
            type: deriveType(item),
            status: deriveStatus(item),
            created_at: item.created_at || '',
            updated_at: item.updated_at || '',
            ai_model: item.ai_model || '',
            parameters: item.parameters || {},
            image_url: item.image_url || '',
            file_url: pptUrl || item.file_url || '',
            // PPT fields
            presentation: presentation || null,
            pptFilename: pptFilename || '',
          };
        });
        setHistoryCache(prev => ({ ...prev, [conversationId]: items }));
        return items;
      }
    } catch { }
    return [];
  }, [historyCache]);

  useEffect(() => { fetchConversations(); }, []);

  const handleRowClick = async (q: QueryRecord) => {
    if (selQuery?.id === q.id) { setSelQuery(null); setPreviewOpen(false); return; }
    if (!q.response) {
      const items = await fetchHistory(q.conversation_id);
      const found = items.find(i => i.id === q.id) || items[0];
      if (found) { setSelQuery(found); setPreviewOpen(true); return; }
    }
    setSelQuery(q);
    setPreviewOpen(true);
  };

  const filtered = useMemo(() =>
    conversations
      .filter(q => statusF === 'all' || q.status === statusF)
      .filter(q => typeF === 'all' || q.type === typeF)
      .filter(q => !search || q.query.toLowerCase().includes(search.toLowerCase()) || q.type.toLowerCase().includes(search.toLowerCase())),
    [conversations, statusF, typeF, search]
  );

  const summary = useMemo(() => ({
    total: filtered.length,
    completed: filtered.filter(q => q.status === 'completed').length,
    wip: filtered.filter(q => q.status === 'wip').length,
    failed: filtered.filter(q => q.status === 'failed').length,
  }), [filtered]);

  const allTypes = useMemo(() => [...new Set(conversations.map(q => q.type).filter(Boolean))], [conversations]);

  const handleDl = (q: QueryRecord) => {
    const content = ['QUERY DETAILS', '─'.repeat(44), `Query    : ${q.query}`, `Type     : ${q.type}`, `Status   : ${q.status}`, `Date     : ${q.created_at}`, `Conv ID  : ${q.conversation_id}`, q.ai_model ? `AI Model : ${q.ai_model}` : '', '', 'AI RESPONSE', '─'.repeat(44), q.response || 'No response content.'].filter(l => l !== undefined).join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `query-${q.conversation_id?.slice(0, 8)}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  const visibleAdmins = role === 'projectAdmin' ? [TEAM_STRUCTURE[0]] : TEAM_STRUCTURE;

  // ── Sidebar content ──────────────────────────────────────────────────────────
  const SidebarContent = () => (
    <Box sx={{ width: { xs: 260, md: 224 }, bgcolor: '#fff', border: { md: '1px solid #e2e8f0' }, borderRadius: { md: '13px' }, overflow: 'hidden', flexShrink: 0 }}>
      <Box sx={{ p: '11px 16px', borderBottom: '1px solid #f1f5f9', bgcolor: '#fafafa' }}>
        <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#374151', letterSpacing: 1.3, textTransform: 'uppercase' }}>
          {role === 'superAdmin' ? 'All Teams' : 'My Team'}
        </Typography>
      </Box>

      <Box onClick={() => { setSelAdmin(null); setSelMember(null); setSelQuery(null); if (isMobile) setSidebarOpen(false); }}
        sx={{ p: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700, bgcolor: !selAdmin ? '#f5f3ff' : 'transparent', color: !selAdmin ? '#7C3AED' : '#1f2937', borderLeft: !selAdmin ? '3px solid #7C3AED' : '3px solid transparent' }}>
        All {role === 'superAdmin' ? 'Admins' : 'Members'}
      </Box>

      {visibleAdmins.map(admin => (
        <Box key={admin.id}>
          <Box onClick={() => { setSelAdmin(admin.id); setSelMember(null); setSelQuery(null); if (isMobile) setSidebarOpen(false); }}
            sx={{ p: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.125, bgcolor: selAdmin === admin.id && !selMember ? '#f5f3ff' : 'transparent', borderLeft: selAdmin === admin.id && !selMember ? '3px solid #7C3AED' : '3px solid transparent', transition: 'all 0.12s' }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: alpha(admin.color, 0.12), color: admin.color, fontSize: 10, fontWeight: 800, border: `1.5px solid ${alpha(admin.color, 0.3)}` }}>{admin.avatar}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin.name}</Typography>
              <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>{admin.team}</Typography>
            </Box>
          </Box>
          {admin.members.map(m => (
            <Box key={m.id} onClick={() => { setSelAdmin(admin.id); setSelMember(m.id); setSelQuery(null); if (isMobile) setSidebarOpen(false); }}
              sx={{ p: '7px 14px 7px 36px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, bgcolor: selMember === m.id ? '#ede9fe' : 'transparent', borderLeft: selMember === m.id ? '3px solid #7C3AED' : '3px solid transparent', transition: 'all 0.12s' }}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(m.color, 0.12), color: m.color, fontSize: 9, fontWeight: 800, border: `1.5px solid ${alpha(m.color, 0.3)}` }}>{m.avatar}</Avatar>
              <Typography sx={{ fontSize: 12, color: selMember === m.id ? '#5b21b6' : '#374151', fontWeight: selMember === m.id ? 700 : 500 }}>{m.name}</Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ fontFamily: "'Inter', system-ui, sans-serif", bgcolor: '#f4f5f7', height: { xs: 'calc(100vh - 30px)', sm: 'calc(100vh - 30px)' }, display: 'flex', flexDirection: 'column' }}>

        {/* TOP HEADER */}
        <AppBar position="static" elevation={0} sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          <Toolbar sx={{ px: { xs: 2, md: 3 }, minHeight: { xs: 56, md: 64 }, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#7C3AED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 900 }}>✦</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f172a', letterSpacing: -0.4 }}>Query History</Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Machadalo Studio · {parsedUser?.first_name || 'User'}</Typography>
              </Box>
            </Box>

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.25 }}>
              {role !== 'individual' && isMobile && (
                <IconButton size="small" onClick={() => setSidebarOpen(true)} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#fff', color: '#374151' }}>
                  <MenuIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}

              <Button variant="outlined" size="small" startIcon={loading ? <CircularProgress size={12} /> : <Refresh sx={{ fontSize: 14 }} />}
                onClick={fetchConversations} disabled={loading}
                sx={{ fontSize: 12, fontWeight: 700, borderRadius: '8px', borderColor: '#e2e8f0', color: loading ? '#94a3b8' : '#374151', '&:hover': { borderColor: '#7C3AED' }, display: { xs: 'none', sm: 'flex' } }}>
                {loading ? 'Loading…' : 'Refresh'}
              </Button>

              {/* Role toggle — hide on xs */}
              {/* <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.375, bgcolor: '#f1f5f9', borderRadius: '10px', p: 0.375 }}>
                {([['superAdmin', 'Super Admin'], ['projectAdmin', 'Project Admin'], ['individual', 'Individual']] as const).map(([k, v]) => (
                  <Button key={k} size="small"
                    onClick={() => { setRole(k); setSelAdmin(null); setSelMember(null); setSelQuery(null); }}
                    sx={{ px: '14px', py: '6px', borderRadius: '8px', border: 'none', fontSize: 12, fontWeight: 700, transition: 'all 0.15s', bgcolor: role === k ? '#fff' : 'transparent', color: role === k ? '#7C3AED' : '#64748b', boxShadow: role === k ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', minWidth: 0, '&:hover': { bgcolor: role === k ? '#fff' : alpha('#7C3AED', 0.06) } }}>
                    {isSmall ? v.split(' ')[0] : v}
                  </Button>
                ))}
              </Box> */}
            </Box>
          </Toolbar>
        </AppBar>

        {/* Error Banner */}
        {error && (
          <Box sx={{ mx: 3, mt: 1.5, p: '10px 16px', bgcolor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#7f1d1d' }}>{error}</Typography>
            <IconButton size="small" onClick={() => setError(null)} sx={{ color: '#7f1d1d' }}><Close sx={{ fontSize: 16 }} /></IconButton>
          </Box>
        )}

        {/* STAT CARDS */}
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatCard label="Total Queries" value={summary.total} color="#7C3AED" icon={<Typography sx={{ fontSize: 20 }}>📋</Typography>} sub={loading ? 'Loading…' : 'all requests'} />
          <StatCard label="Completed" value={summary.completed} color="#16a34a" icon={<CheckCircle sx={{ color: '#16a34a', fontSize: 22 }} />} sub={summary.total ? Math.round((summary.completed / summary.total) * 100) + '% success rate' : '—'} />          <StatCard label="In Progress" value={summary.wip} color="#ca8a04" icon={<HourglassEmpty sx={{ color: '#ca8a04', fontSize: 22 }} />} sub="being processed" />
          <StatCard label="Failed" value={summary.failed} color="#dc2626" icon={<Cancel sx={{ color: '#dc2626', fontSize: 22 }} />} sub={summary.total ? Math.round(summary.failed / summary.total * 100) + '% failure rate' : '—'} />
        </Box>

        {/* BODY */}
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 1.75, pb: 3, flex: 1, display: 'flex', gap: 1.75, alignItems: 'flex-start', overflow: 'hidden', minHeight: 0 }}>

          {/* Desktop Sidebar */}
          {role !== 'individual' && !isMobile && <SidebarContent />}

          {/* Mobile Sidebar Drawer */}
          {role !== 'individual' && isMobile && (
            <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
              <Box sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 0.5 }}>
                  <IconButton size="small" onClick={() => setSidebarOpen(false)}><Close /></IconButton>
                </Box>
                <SidebarContent />
              </Box>
            </Drawer>
          )}

          {/* CENTER + PREVIEW */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', gap: 1.75, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' }, height: '100%', overflow: 'hidden' }}>

            {/* TABLE PANEL */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', height: '100%', overflow: 'hidden' }}>

              {/* FILTER BAR */}
              <Paper variant="outlined" sx={{ borderRadius: '12px', p: '11px 16px', display: 'flex', gap: 1.125, alignItems: 'center', flexWrap: 'wrap', border: '1px solid #e2e8f0' }}>
                <TextField
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search queries or types…"
                  size="small" variant="outlined"
                  sx={{ flex: '1 1 200px', '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 12.5, bgcolor: '#f8fafc', fontWeight: 500 } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
                />
                <Select value={statusF} onChange={e => setStatusF(e.target.value)} size="small" displayEmpty
                  sx={{ fontSize: 12, fontWeight: 700, color: '#1e293b', bgcolor: '#f8fafc', borderRadius: '8px', minWidth: 120, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' } }}>
                  <MenuItem value="all" sx={{ fontSize: 12, fontWeight: 700 }}>All Status</MenuItem>
                  <MenuItem value="completed" sx={{ fontSize: 12, fontWeight: 700 }}>Completed</MenuItem>
                  <MenuItem value="wip" sx={{ fontSize: 12, fontWeight: 700 }}>In Progress</MenuItem>
                  <MenuItem value="failed" sx={{ fontSize: 12, fontWeight: 700 }}>Failed</MenuItem>
                </Select>
                <Select value={typeF} onChange={e => setTypeF(e.target.value)} size="small" displayEmpty
                  sx={{ fontSize: 12, fontWeight: 700, color: '#1e293b', bgcolor: '#f8fafc', borderRadius: '8px', maxWidth: 180, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' } }}>
                  <MenuItem value="all" sx={{ fontSize: 12, fontWeight: 700 }}>All Types</MenuItem>
                  {allTypes.map(t => <MenuItem key={t} value={t} sx={{ fontSize: 12, fontWeight: 700 }}>{t}</MenuItem>)}
                </Select>
              </Paper>

              {/* TABLE */}
              <Paper variant="outlined" sx={{ borderRadius: '13px', overflow: 'hidden', border: '1px solid #e2e8f0', flex: 1 }}>
                {loading ? (
                  <Box sx={{ p: '52px 24px', textAlign: 'center' }}>
                    <CircularProgress size={28} sx={{ color: '#7C3AED', mb: 1.25 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Loading your queries…</Typography>
                  </Box>
                ) : (
                  <CommonTable
                    columns={columns}
                    data={filtered}
                    isLoading={loading}
                    enableBottomToolbar={false}
                    enableTopToolbar={false}
                    muiTableContainerProps={{
                      sx: { maxHeight: 'calc(100vh - 330px)', width: '100%' },
                    }}
                    state={{ isLoading: loading, sorting: [] }}
                    initialState={{ density: 'compact' }}
                  />
                )}
              </Paper>
            </Box>

            {/* PREVIEW PANEL — desktop inline, mobile as Drawer */}
            {selQuery && !isMobile && (
              <PreviewPanel
                query={selQuery}
                onClose={() => setSelQuery(null)}
                onDl={handleDl}
                onShare={setShareQ}
                feedback={getFeedback(selQuery.id)}
                onFeedbackChange={changes => updateFeedback(selQuery.id, changes)}
                onFeedbackSubmit={() => submitFeedback(selQuery.id)}
              />
            )}

            {selQuery && isMobile && (
              <Drawer anchor="bottom" open={previewOpen} onClose={() => { setPreviewOpen(false); setSelQuery(null); }}
                PaperProps={{ sx: { borderRadius: '16px 16px 0 0', maxHeight: '85vh' } }}>
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ width: 36, height: 4, bgcolor: '#e2e8f0', borderRadius: '2px' }} />
                </Box>
                <Box sx={{ overflow: 'auto', flex: 1 }}>
                  <PreviewPanel
                    query={selQuery}
                    onClose={() => { setPreviewOpen(false); setSelQuery(null); }}
                    onDl={handleDl}
                    onShare={setShareQ}
                    feedback={getFeedback(selQuery.id)}
                    onFeedbackChange={changes => updateFeedback(selQuery.id, changes)}
                    onFeedbackSubmit={() => submitFeedback(selQuery.id)}
                  />
                </Box>
              </Drawer>
            )}
          </Box>
          {feedbackModalQuery && (
            <FeedbackSection
              queryId={feedbackModalQuery.id}
              feedback={getFeedback(feedbackModalQuery.id)}
              onChange={(changes) => updateFeedback(feedbackModalQuery.id, changes)}
              onSubmit={async () => {
                await submitFeedback(feedbackModalQuery.id);
                setFeedbackModalQuery(null);
              }}
              forceOpen={true}
              onClose={() => setFeedbackModalQuery(null)}
            />
          )}
        </Box>

        {shareQ && <ShareModal query={shareQ} onClose={() => setShareQ(null)} />}

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} sx={{ fontWeight: 700, fontSize: 13 }}>{snackbar.message}</Alert>
        </Snackbar>

        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');`}</style>
      </Box>
    </ThemeProvider>
  );
};

export default QueryHistoryDashboard;